import { useEffect, useState } from 'react';
import { ref, set, remove, onDisconnect, serverTimestamp } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { rtdb } from '../../providers/firebase/firebase';

const HEARTBEAT_MS = 30_000;

function deviceId(): string {
    let id = localStorage.getItem('app_device_id');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('app_device_id', id); }
    return id;
}

/**
 * While this tab is visible AND focused, publish device presence at
 * presence/app/{uid}/{deviceId} = { fcm_token, last_active }, heartbeating
 * last_active every 30s. The fan-out routes pushes to the active device only.
 * onDisconnect clears the node on socket loss (re-armed each beat);
 * visibility/blur clears it when the user navigates away without closing.
 */
export function useAppPresence(fcmToken: string | null | undefined) {
    const [uid, setUid] = useState<string | undefined>(() => getAuth().currentUser?.uid ?? undefined);

    useEffect(() => {
        const auth = getAuth();
        setUid(auth.currentUser?.uid ?? undefined);
        return onAuthStateChanged(auth, u => setUid(u?.uid ?? undefined));
    }, []);

    useEffect(() => {
        if (!uid || !fcmToken) return;
        const node = ref(rtdb, `presence/app/${uid}/${deviceId()}`);
        let timer: ReturnType<typeof setInterval> | null = null;

        const isActive = () => document.visibilityState === 'visible' && document.hasFocus();
        const beat = () => { set(node, { fcm_token: fcmToken, last_active: serverTimestamp() }); onDisconnect(node).remove(); };
        const goActive = () => { beat(); if (!timer) timer = setInterval(() => { if (isActive()) beat(); }, HEARTBEAT_MS); };
        const goInactive = () => { if (timer) { clearInterval(timer); timer = null; } remove(node); };
        const onChange = () => (isActive() ? goActive() : goInactive());

        if (isActive()) goActive();
        document.addEventListener('visibilitychange', onChange);
        window.addEventListener('focus', onChange);
        window.addEventListener('blur', onChange);

        return () => {
            document.removeEventListener('visibilitychange', onChange);
            window.removeEventListener('focus', onChange);
            window.removeEventListener('blur', onChange);
            if (timer) clearInterval(timer);
            remove(node);
        };
    }, [uid, fcmToken]);
}
